import { NotificationType } from '../services/api/notifications/notificationsApi';

/**
 * Configuração de navegação para cada tipo de notificação
 */
export interface NavigationConfig {
  page: string;
  params: Record<string, string | number | boolean>;
}

/**
 * Mapeamento de tipos de notificação para configurações de navegação
 */
export const notificationNavigationMap: Record<NotificationType, NavigationConfig> = {
  'PROJECT_ASSIGNED': {
    page: '/activities',
    params: { activityId: 'entityId', openActivity: true }
  },
  'PAYMENT_RECEIVED': {
    page: '/projects/entityId',
    params: {}
  },
  'PAYMENT_OVERDUE': {
    page: '/projects/entityId',
    params: {}
  },
  'PROJECT_AUTO_CREATED': {
    page: '/activities',
    params: { activityId: 'entityId', openActivity: true }
  },
  'STAGE_CHANGED': {
    page: '/activities',
    params: { activityId: 'entityId', openActivity: true }
  },
  'COMMENT_ADDED': {
    page: '/projects/entityId',
    params: {}
  },
  'COMMENT_MENTION': {
    page: '/projects/entityId',
    params: {}
  }
};

/**
 * Constrói uma URL com query parameters baseada na configuração de navegação
 * Suporta path params substituindo 'entityId' no caminho
 * @param page - Página de destino (pode conter 'entityId' como placeholder)
 * @param params - Parâmetros da configuração
 * @param entityId - ID da entidade (projeto)
 * @returns URL completa com query parameters e/ou path params
 */
export const buildNavigationUrl = (
  page: string, 
  params: Record<string, string | number | boolean>, 
  entityId: number
): string => {
  // Substituir entityId no path se existir
  let finalPage = page.replace('entityId', entityId.toString());
  
  const urlParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value === 'entityId') {
      urlParams.set(key, entityId.toString());
    } else {
      urlParams.set(key, value.toString());
    }
  });
  
  const queryString = urlParams.toString();
  return queryString ? `${finalPage}?${queryString}` : finalPage;
};

/**
 * Obtém a configuração de navegação para um tipo de notificação
 * @param type - Tipo da notificação
 * @returns Configuração de navegação ou null se não encontrada
 */
export const getNavigationConfig = (type: NotificationType): NavigationConfig | null => {
  return notificationNavigationMap[type] || null;
};

/**
 * Constrói URL de navegação para uma notificação
 * @param type - Tipo da notificação
 * @param entityId - ID da entidade (projeto)
 * @returns URL completa para navegação
 */
export const buildNotificationNavigationUrl = (type: NotificationType, entityId: number): string | null => {
  const config = getNavigationConfig(type);
  if (!config) return null;
  
  return buildNavigationUrl(config.page, config.params, entityId);
};


/**
 * Utilitários para trabalhar com query parameters
 */
export const queryParamsUtils = {
  /**
   * Obtém valor de um query parameter como string
   */
  getString: (searchParams: URLSearchParams, key: string, defaultValue?: string): string | undefined => {
    return searchParams.get(key) || defaultValue;
  },
  
  /**
   * Obtém valor de um query parameter como número
   */
  getNumber: (searchParams: URLSearchParams, key: string, defaultValue?: number): number | undefined => {
    const value = searchParams.get(key);
    return value ? parseInt(value, 10) : defaultValue;
  },
  
  /**
   * Obtém valor de um query parameter como boolean
   */
  getBoolean: (searchParams: URLSearchParams, key: string, defaultValue?: boolean): boolean | undefined => {
    const value = searchParams.get(key);
    return value ? value === 'true' : defaultValue;
  },
  
  /**
   * Obtém valor de um query parameter como array de strings
   */
  getArray: (searchParams: URLSearchParams, key: string, defaultValue?: string[]): string[] | undefined => {
    const value = searchParams.get(key);
    return value ? value.split(',') : defaultValue;
  },
  
  /**
   * Cria URLSearchParams a partir de um objeto
   */
  createFromObject: (params: Record<string, string | number | boolean>): URLSearchParams => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value.toString());
    });
    return searchParams;
  }
};
